import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Card,
    CardBody,
    Button,
    Label,
    Input,
    FormGroup,
    Row,
    Col,
    Spinner,
} from "reactstrap";
import { ToastContainer, toast } from "react-toastify";
import Breadcrumbs from "../../Components/Common/Breadcrumb";
import AxiosInstance from "../../services/AxiosInstance";
import "react-toastify/dist/ReactToastify.css";

const AddUser = () => {
    const navigate = useNavigate();
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    const isSuperDenied = userData.is_super === 0 || userData.is_super === "0";

    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        firstname: "",
        lastname: "",
        email: "",
        mobile_no: "",
        password: "",
        user_role_id: "",
    });
    const [errors, setErrors] = useState({});

    const fetchRoles = useCallback(async () => {
        try {
            const res = await AxiosInstance().post("/admin/role/get_roles", {});
            setRoles(Array.isArray(res.data?.data) ? res.data.data : []);
        } catch (e) {
            console.error(e);
            toast.error(e.response?.data?.message || "Failed to load roles");
            setRoles([]);
        }
    }, []);

    useEffect(() => {
        if (isSuperDenied) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        const load = async () => {
            await fetchRoles();
            if (!cancelled) setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [isSuperDenied, fetchRoles]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const validate = () => {
        const err = {};
        if (!formData.firstname?.trim()) err.firstname = "First name is required";
        if (!formData.lastname?.trim()) err.lastname = "Last name is required";
        if (!formData.email?.trim()) err.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,6}$/.test(formData.email)) err.email = "Enter a valid email";
        if (!formData.password) err.password = "Password is required";
        else if (formData.password.length < 6) err.password = "Password must be at least 6 characters";
        if (!formData.user_role_id) err.user_role_id = "Please select a role";
        setErrors(err);
        return Object.keys(err).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const res = await AxiosInstance().post("/admin/user/create", {
                firstname: formData.firstname.trim(),
                lastname: formData.lastname.trim(),
                email: formData.email.trim(),
                mobile_no: (formData.mobile_no || "").trim(),
                password: formData.password,
                user_role_id: parseInt(formData.user_role_id, 10),
            });
            if (res.data.status) {
                toast.success(res.data.message || "User created successfully");
                navigate("/job/admin/manage-users");
            } else {
                toast.error(res.data.message || "Failed to create user");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Request failed");
        } finally {
            setSaving(false);
        }
    };

    const goBack = () => navigate("/job/admin/manage-users");

    if (isSuperDenied) {
        return (
            <Fragment>
                <Breadcrumbs title="Add User" mainTitle="Admin" />
                <Container fluid className="p-4">
                    <Card className="border-0 shadow-sm">
                        <CardBody className="text-center py-5">
                            <i className="fa fa-lock fa-3x text-warning mb-3" />
                            <h5 className="text-danger mb-2">Access Denied</h5>
                            <p className="text-muted mb-0">Only Super Admin can add users.</p>
                            <Button color="primary" className="mt-3" onClick={goBack}>Back to Users</Button>
                        </CardBody>
                    </Card>
                </Container>
            </Fragment>
        );
    }

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs title="Add User" mainTitle="Admin" />
                <div className="d-flex justify-content-center align-items-center py-5">
                    <Spinner color="primary" />
                </div>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs title="Add User" mainTitle="Admin" />
            <Container fluid className="p-4">
                <div className="d-flex align-items-center gap-2 mb-4">
                    <Button color="light" size="sm" onClick={goBack}>
                        <i className="fa fa-arrow-left me-1" /> Back
                    </Button>
                </div>
                <h5 className="mb-4 fw-bold">Add New User</h5>

                <Card className="border shadow-none mb-4">
                    <CardBody className="p-4">
                        <form onSubmit={handleSubmit}>
                            <Row className="g-3">
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="firstname" className="mb-2">First name <span className="text-danger">*</span></Label>
                                        <Input
                                            id="firstname"
                                            name="firstname"
                                            type="text"
                                            value={formData.firstname}
                                            onChange={handleChange}
                                            placeholder="First name"
                                            invalid={!!errors.firstname}
                                        />
                                        {errors.firstname && <small className="text-danger">{errors.firstname}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="lastname" className="mb-2">Last name <span className="text-danger">*</span></Label>
                                        <Input
                                            id="lastname"
                                            name="lastname"
                                            type="text"
                                            value={formData.lastname}
                                            onChange={handleChange}
                                            placeholder="Last name"
                                            invalid={!!errors.lastname}
                                        />
                                        {errors.lastname && <small className="text-danger">{errors.lastname}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="email" className="mb-2">Email <span className="text-danger">*</span></Label>
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="user@example.com"
                                            invalid={!!errors.email}
                                        />
                                        {errors.email && <small className="text-danger">{errors.email}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="mobile_no" className="mb-2">Mobile</Label>
                                        <Input
                                            id="mobile_no"
                                            name="mobile_no"
                                            type="text"
                                            value={formData.mobile_no}
                                            onChange={handleChange}
                                            placeholder="Optional"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="password" className="mb-2">Password <span className="text-danger">*</span></Label>
                                        <Input
                                            id="password"
                                            name="password"
                                            type="password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Min 6 characters"
                                            invalid={!!errors.password}
                                        />
                                        {errors.password && <small className="text-danger">{errors.password}</small>}
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="mb-3">
                                        <Label for="user_role_id" className="mb-2">Role <span className="text-danger">*</span></Label>
                                        <Input
                                            id="user_role_id"
                                            name="user_role_id"
                                            type="select"
                                            value={formData.user_role_id}
                                            onChange={handleChange}
                                            invalid={!!errors.user_role_id}
                                        >
                                            <option value="">Select role</option>
                                            {roles.map((r) => (
                                                <option key={r.user_role_id} value={r.user_role_id}>{r.user_role_title}</option>
                                            ))}
                                        </Input>
                                        {errors.user_role_id && <small className="text-danger">{errors.user_role_id}</small>}
                                    </FormGroup>
                                </Col>
                            </Row>

                            <div className="d-flex justify-content-end gap-3 mt-4 pt-4 border-top">
                                <Button type="button" color="secondary" onClick={goBack}>Cancel</Button>
                                <Button type="submit" color="primary" disabled={saving}>
                                    {saving ? "Creating..." : "Create User"}
                                </Button>
                            </div>
                        </form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </Fragment>
    );
};

export default AddUser;
